'use strict';

const SELECTOR_ROOT = '.js-projects-list';
const SELECTOR_MOBILE_FILTER = '.js-projects-list-mobile-filter';
const PARAM_CATEGORY = 'projectsCategory';
const PARAM_PAGE = 'projectsPage';

const unique = (array) => [...new Set(array)];
const slugify = (value) => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/(^-|-$)/g, '');

const initOne = (root) => {
  if (!(root instanceof Element) || root.dataset.projectsListInitialized === '1') {
    return;
  }

  const filtersContainer = root.querySelector('.js-projects-list-filters');
  const allButton = root.querySelector('.js-projects-list-filter-all');
  const prevButton = root.querySelector('.js-projects-list-prev');
  const nextButton = root.querySelector('.js-projects-list-next');
  const cards = Array.from(root.querySelectorAll('.js-projects-list-item'));
  const perPageValue = Number.parseInt(root.dataset.perPage || '', 10);
  const perPage = Number.isFinite(perPageValue) && perPageValue > 0 ? perPageValue : 6;

  if (!(filtersContainer instanceof Element) || !(allButton instanceof HTMLButtonElement) || !cards.length) {
    return;
  }

  const categoryTitles = unique(
    cards
      .map((card) => (card.dataset.category || '').trim())
      .filter((value) => value.length > 0)
  );

  categoryTitles.forEach((categoryTitle) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tc-website-projects-list__filter js-projects-list-filter';
    button.dataset.category = categoryTitle;
    button.textContent = categoryTitle;
    filtersContainer.appendChild(button);
  });

  let activeCategory = '__all';
  let currentPage = 1;
  let isApplyingPopState = false;

  const slugToCategoryMap = new Map();
  categoryTitles.forEach((title) => {
    slugToCategoryMap.set(slugify(title), title);
  });

  const readStateFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const categorySlug = (params.get(PARAM_CATEGORY) || '').trim();
    const pageValue = Number.parseInt(params.get(PARAM_PAGE) || '', 10);
    const nextCategory = categorySlug && slugToCategoryMap.has(categorySlug)
      ? slugToCategoryMap.get(categorySlug)
      : '__all';
    const nextPage = Number.isFinite(pageValue) && pageValue > 0 ? pageValue : 1;
    return { category: nextCategory, page: nextPage };
  };

  const writeStateToUrl = () => {
    if (isApplyingPopState) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (activeCategory === '__all') {
      params.delete(PARAM_CATEGORY);
    } else {
      params.set(PARAM_CATEGORY, slugify(activeCategory));
    }
    if (currentPage <= 1) {
      params.delete(PARAM_PAGE);
    } else {
      params.set(PARAM_PAGE, String(currentPage));
    }

    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
    window.history.pushState({ [PARAM_CATEGORY]: activeCategory, [PARAM_PAGE]: currentPage }, '', nextUrl);
  };

  const getFilteredCards = () => {
    if (activeCategory === '__all') {
      return cards;
    }
    return cards.filter((card) => (card.dataset.category || '').trim() === activeCategory);
  };

  const render = () => {
    const filteredCards = getFilteredCards();
    const totalPages = Math.max(1, Math.ceil(filteredCards.length / perPage));
    if (currentPage < 1) {
      currentPage = 1;
    }
    if (currentPage > totalPages) {
      currentPage = totalPages;
    }

    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    const visibleSet = new Set(filteredCards.slice(start, end));

    cards.forEach((card) => {
      card.style.display = visibleSet.has(card) ? '' : 'none';
    });

    const buttons = root.querySelectorAll('.js-projects-list-filter, .js-projects-list-filter-all');
    buttons.forEach((button) => {
      if (!(button instanceof HTMLElement)) {
        return;
      }
      const isActive = (button.dataset.category || '') === activeCategory;
      button.classList.toggle('is-active', isActive);
    });

    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled = currentPage <= 1;
    }
    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled = currentPage >= totalPages;
    }

    writeStateToUrl();
  };

  filtersContainer.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLButtonElement)) {
      return;
    }
    if (!target.classList.contains('js-projects-list-filter') && !target.classList.contains('js-projects-list-filter-all')) {
      return;
    }
    activeCategory = target.dataset.category || '__all';
    currentPage = 1;
    render();
  });

  if (prevButton instanceof HTMLButtonElement) {
    prevButton.addEventListener('click', () => {
      currentPage = Math.max(1, currentPage - 1);
      render();
    });
  }

  if (nextButton instanceof HTMLButtonElement) {
    nextButton.addEventListener('click', () => {
      currentPage += 1;
      render();
    });
  }

  const initialState = readStateFromUrl();
  activeCategory = initialState.category;
  currentPage = initialState.page;

  window.addEventListener('popstate', () => {
    isApplyingPopState = true;
    const nextState = readStateFromUrl();
    activeCategory = nextState.category;
    currentPage = nextState.page;
    render();
    isApplyingPopState = false;
  });

  render();
  root.dataset.projectsListInitialized = '1';
};

const init = () => {
  const mobileFilters = document.querySelectorAll(SELECTOR_MOBILE_FILTER);
  mobileFilters.forEach((select) => {
    if (!(select instanceof HTMLSelectElement) || select.dataset.projectsListMobileInitialized === '1') {
      return;
    }

    select.addEventListener('change', () => {
      const { value } = select;
      if (value) {
        window.location.assign(value);
      }
    });

    select.dataset.projectsListMobileInitialized = '1';
  });

  const roots = document.querySelectorAll(SELECTOR_ROOT);
  if (!roots.length) {
    return;
  }
  roots.forEach(initOne);
};

export default { init };
